import { Component, HostListener, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TabViewComponent, TabItem } from '../components/tab-view/tab-view.component';
import { BaseChartDirective } from 'ng2-charts';
import { Subject } from 'rxjs';
import { takeUntil, skip } from 'rxjs/operators';

import { RevenueTabComponent } from './tabs/revenue-tab/revenue-tab.component';
import { CustomerTabComponent } from './tabs/customer-tab/customer-tab.component';
import { CarWashTabComponent } from './tabs/car-wash-tab/car-wash-tab.component';
import { CampaignTabComponent } from './tabs/campaign-tab/campaign-tab.component';
import { DashboardStatData, ServiceOptinStatData, NewVsReturningData } from '../shared/types';
import { ProfileService } from '../services/profile.service';
import { LocationService } from '../services/location.service';
import {
  getDashboardDoughnutChartOptions,
  getDashboardChartTooltipWithPercentOfTotal,
  getDashboardLineChartOptionsWithPercentChange,
  getDashboardTooltipLabelWithPercentChangeAndFormat,
  getDashboardLineChartOptionsWithCustomTooltip,
  getDashboardLineChartPointStyle,
  getDashboardWashesByHourChartOptions,
  createDashboardLineDataset,
} from './dashboard-chart.constants';
import { calculateDynamicMax } from '../shared/utils/chart.utils';
import {
  DashboardDataService,
  SelectOption,
  CustomerTrendResult,
  ChartLabelsData,
} from './services/dashboard-data.service';
import { DateRange } from '../shared/components/date-range-picker/date-range-picker.component';
import {
  DASHBOARD_PERIOD_OPTIONS,
  buildPeriodApiParam,
  dashboardPeriodLabel,
} from '../shared/utils/dashboard-period-filter.util';

const DASHBOARD_CHART_X_SCALE = {
  grid: { display: false },
  border: { display: false },
  ticks: {
    color: '#555963',
    font: { family: 'Inter', size: 12, weight: 500 },
  },
};

const DASHBOARD_CHART_Y_SCALE_HIDDEN = {
  display: false,
  grid: { display: false },
  border: { display: false },
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    TabViewComponent,
    CommonModule,
    BaseChartDirective,
    RevenueTabComponent,
    CustomerTabComponent,
    CarWashTabComponent,
    CampaignTabComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private isInitializing = true;
  private lastLoadedLocation = '';
  private lastLoadedTab = '';
  private isLoadingData = false;

  activeTab = 'Revenue';
  isDropdownOpen = false;
  selectedPeriod = 'month';
  customDateRange: DateRange = { startDate: null, endDate: null };
  isLocationDropdownOpen = false;
  selectedLocation = '';
  selectedLocationLabel = '';
  locationOptions: SelectOption[] = [];

  isInitialLoad = true;
  hasAppliedFilters = false;

  isCarWashDropdownOpen = false;
  selectedCarWashPeriod = 'Today';

  isCampaignDropdownOpen = false;
  selectedCampaignPeriod = 'Today';
  isCampaignTypeDropdownOpen = false;
  selectedCampaignType = '';
  isCampaignNameDropdownOpen = false;
  selectedCampaignName = '';

  dashboardStats: DashboardStatData[] = [
    {
      title: 'Total Revenue',
      value: '0',
      percentage: '0',
      periodText: '',
      gradient: 'linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)',
      icon: 'assets/dashboard/r-1.png',
      tooltip: 'Total revenue generated from the selected location in the selected period.',
    },
    {
      title: 'Cash Collection',
      value: '0',
      percentage: '0',
      periodText: '',
      gradient: 'linear-gradient(rgb(142, 217, 246) 0%, rgb(73, 163, 215) 100%)',
      icon: 'assets/dashboard/r-1.png',
      tooltip: 'Total cash payments collected in the selected period.',
    },
    {
      title: 'Card Collection',
      value: '0',
      percentage: '0',
      periodText: '',
      gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
      icon: 'assets/dashboard/r-1.png',
      tooltip: 'Total card payments collected in the selected period.',
    },
    {
      title: 'Prepaid Order Count',
      value: '0',
      percentage: '0',
      periodText: '',
      gradient: 'linear-gradient(rgb(121, 136, 223) 0%, rgb(59, 109, 218) 100%)',
      icon: 'assets/dashboard/r-2.png',
      tooltip: 'Number of prepaid wash books sold (paid) in the selected period.',
    },
    {
      title: 'Avg. Revenue/Wash',
      value: '0',
      percentage: '0',
      periodText: '',
      gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
      icon: 'assets/dashboard/r-3.png',
      tooltip: 'Average revenue earned per car wash in the selected period.',
    },
    {
      title: 'Upsell Conversion',
      value: '0',
      percentage: '0',
      periodText: '',
      gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
      icon: 'assets/dashboard/r-4.png',
      tooltip: 'Percentage of car washes that resulted in an upsell during the selected period.',
    },
  ];

  customerDashboardStats: DashboardStatData[] = [
    {
      title: 'Total Active Members',
      value: '0',
      percentage: '0',
      periodText: '',
      gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)',
      icon: 'assets/dashboard/c-1.png',
      tooltip: 'Number of customers with an active membership.',
    },
    {
      title: 'Customer Satisfaction',
      value: '0',
      percentage: '',
      periodText: '',
      gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
      icon: 'assets/dashboard/c-2.png',
      tooltip: 'Average customer rating based on the latest available feedback.',
    },
    {
      title: 'Membership Renewal',
      value: '0',
      percentage: '',
      periodText: '',
      gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
      icon: 'assets/dashboard/c-3.png',
      tooltip: 'Percentage of membership renewal charges that succeeded in the selected period.',
    },
  ];

  newVsReturningData: NewVsReturningData = {
    title: 'New vs Returning',
    newCustomers: { count: '0', percentage: '0%' },
    returningCustomers: { count: '0', percentage: '0%' },
    gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
    tooltip: 'Number of new vs returning customers each month.',
  };

  serviceOptinStats: ServiceOptinStatData[] = [
    {
      title: 'Oil Change Opt-in',
      value: '1,800',
      subText: 'customers enrolled',
      backgroundColor: '#FFFFFF',
      textColor: '#15172E',
      valueColor: '#73A0E9',
      icon: 'assets/dashboard/c-4.png',
      tooltip: 'Total number of customers enrolled for oil change services.',
    },
    {
      title: 'Insurance Coverage',
      value: '$33,87,930',
      subText: '$10k AD&D coverage',
      backgroundColor: '#FFFFFF',
      textColor: '#15172E',
      valueColor: '#B473E9',
      icon: 'assets/dashboard/c-5.png',
      tooltip: 'Total number of customers with $10K AD&D insurance coverage.',
    },
    {
      title: 'Gas Opt-in',
      value: '$10,39,155',
      subText: 'gas savings',
      backgroundColor: '#FFFFFF',
      textColor: '#15172E',
      valueColor: '#E973C6',
      icon: 'assets/dashboard/c-6.png',
      tooltip: 'Total gas savings received by customers.',
    },
    {
      title: 'Roadside Opt-in',
      value: '1,586 mi',
      subText: 'free miles received',
      backgroundColor: '#FFFFFF',
      textColor: '#15172E',
      valueColor: '#0D9A93',
      icon: 'assets/dashboard/c-7.png',
      tooltip: 'Total miles received by customers enrolled for roadside assistance.',
    },
  ];

  carWashDashboardStats: DashboardStatData[] = [
    {
      title: 'Total Washes',
      value: '0',
      percentage: '',
      periodText: 'Today',
      gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)',
      icon: 'assets/dashboard/w-1.png',
      tooltip:
        'Car washes completed in the selected time period: single washes, membership redemptions, prepaid redemptions, rewashes and fleet washes.',
    },
    {
      title: 'Avg. Washes/Hr',
      value: '0',
      percentage: '',
      periodText: 'Based on operating hours',
      gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
      icon: 'assets/dashboard/w-2.png',
      tooltip: 'Average number of car washes completed every hour.',
    },
    {
      title: 'Popular Service',
      value: '-',
      percentage: '',
      periodText: '',
      gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
      icon: 'assets/dashboard/w-4.png',
      tooltip: 'The most frequently purchased wash service by customers.',
    },
  ];

  washesByHourData: any;
  washesByHourOptions: any;
  washesByServiceTypeData: any;
  washesByServiceTypeOptions: any;

  campaignDashboardStats: DashboardStatData[] = [
    {
      title: 'Emails',
      value: '0/0',
      percentage: '',
      periodText: 'Sent/Total',
      gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)',
      icon: 'assets/dashboard/cm-1.png',
      tooltip: 'Total number of marketing emails sent vs planned.',
      showProgressBar: true,
      progressPercentage: 62.5,
    },
    {
      title: 'Text Messages',
      value: '0/0',
      percentage: '',
      periodText: 'Sent/Total',
      gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
      icon: 'assets/dashboard/cm-2.png',
      tooltip: 'Total number of Text Messages sent vs planned.',
      showProgressBar: true,
      progressPercentage: 60,
    },
    {
      title: 'Marketing Spend',
      value: '0',
      percentage: '',
      periodText: 'Today',
      gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
      icon: 'assets/dashboard/cm-3.png',
      tooltip: 'Total amount spent for marketing campaigns in the selected period.',
    },
    {
      title: 'Avg. Conversion',
      value: '0',
      percentage: '',
      periodText: 'Campaign performance',
      gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
      icon: 'assets/dashboard/cm-4.png',
      tooltip: 'Percentage of users who purchased a wash after engaging with the campaign.',
    },
  ];

  campaignBottomStats: DashboardStatData[] = [
    {
      title: 'Returning Customers',
      value: '0',
      percentage: '',
      periodText: '',
      gradient: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)',
      icon: 'assets/dashboard/c-1.png',
      tooltip: 'Number of customers who returned for another wash during the selected period.',
      textColor: '#73A0E9',
    },
    {
      title: 'Revenue Generated',
      value: '0',
      percentage: '',
      periodText: '',
      gradient: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)',
      icon: 'assets/dashboard/r-1.png',
      tooltip: 'Total revenue from completed car washes in the selected period.',
      textColor: '#079271',
    },
  ];

  campaignPerformanceTrendData: any;
  campaignPerformanceTrendOptions: any;
  campaignFunnelData: any;
  campaignFunnelOptions: any;
  campaignNameOptions: SelectOption[] = [];
  platformOptions: SelectOption[] = [];

  topSellingService = { name: '-', unitsSold: 0, revenue: 0 };

  revenueByServiceData: any;
  revenueByServiceOptions: any;
  revenueTrendData: any;
  revenueTrendOptions: any;
  avgRevenuePerTeamData: any;
  avgRevenuePerTeamOptions: any;
  revenueStatsData: any;
  customerStatsData: any;
  carWashStatsData: any;

  newVsReturningTrendData: any;
  newVsReturningTrendOptions: any;
  visitFrequencyData: any;
  visitFrequencyOptions: any;
  monthlyAcquisitionData: any;
  monthlyAcquisitionOptions: any;

  dashboardTabs: TabItem[] = [
    { name: 'Revenue' },
    { name: 'Customer' },
    { name: 'Car Wash' },
    { name: 'Campaign Management' },
  ];

  periodOptions = [...DASHBOARD_PERIOD_OPTIONS];

  get selectedPeriodLabel(): string {
    return dashboardPeriodLabel(this.selectedPeriod);
  }

  constructor(
    private readonly profileService: ProfileService,
    private readonly locationService: LocationService,
    private readonly dashboardDataService: DashboardDataService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.initChartData();
  }

  /**
   * Initializes the dashboard state from query parameters and location data, keeps the active tab and selected location in sync, and triggers data loading when needed.
   * @example
   * gOnInit()
   * void
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    const initialParams = this.route.snapshot.queryParams;
    if (initialParams['tab']) {
      this.activeTab = initialParams['tab'];
    }

    this.route.queryParams.pipe(skip(1), takeUntil(this.destroy$)).subscribe(params => {
      const tab = params['tab'];
      if (tab && tab !== this.activeTab) {
        this.activeTab = tab;
        if (!this.isInitializing && this.selectedLocation) {
          this.loadDataForActiveTab();
        }
      }
    });

    this.profileService.locations$.pipe(takeUntil(this.destroy$)).subscribe(locations => {
      if (!this.isDashboardRoute()) return;

      this.locationOptions = (locations || []).map((loc: any) => ({
        label: loc.branch,
        value: loc.id,
      }));

      const isCurrentLocationValid =
        this.selectedLocation &&
        this.locationOptions.some(opt => opt.value === this.selectedLocation);
      const previousLocation = this.selectedLocation;

      if (!this.selectedLocation || !isCurrentLocationValid) {
        if (this.locationOptions.length > 0) {
          this.selectedLocation = this.locationOptions[0].value;
          this.selectedLocationLabel = this.locationOptions[0].label;
        } else {
          this.selectedLocation = '';
          this.selectedLocationLabel = 'No Location';
        }
      } else {
        const match = this.locationOptions.find(opt => opt.value === this.selectedLocation);
        this.selectedLocationLabel = match ? match.label : this.selectedLocation;
      }

      this.locationService.setShopLocationById(this.selectedLocation);

      const locationChanged = this.selectedLocation !== previousLocation;
      const shouldLoad =
        this.selectedLocation &&
        (locationChanged ||
          (this.isInitializing &&
            (this.lastLoadedLocation !== this.selectedLocation ||
              this.lastLoadedTab !== this.activeTab)));

      if (shouldLoad && !this.isLoadingData) {
        this.loadDataForActiveTab();
      }

      if (this.isInitializing) {
        this.isInitialLoad = false;
        this.isInitializing = false;
      }
    });

    this.profileService.loadLocations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Tab & Period Handlers ────────────────────────────────────────────────

  /**
   * Handles tab changes, updates the active tab state, and triggers data loading when needed.
   * @example
   * nTabChange('overview')
   * void
   * @param {string} tabName - The name of the tab to switch to.
   * @returns {void} Does not return a value.
   **/
  onTabChange(tabName: string) {
    if (this.isInitializing && tabName === this.activeTab) return;
    if (tabName === this.activeTab) return;

    this.activeTab = tabName;
    this.isLoadingData = false;
    this.lastLoadedTab = '';

    const currentTab = this.route.snapshot.queryParams['tab'];
    if (currentTab !== tabName) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: tabName },
        queryParamsHandling: 'merge',
      });
    }

    if (!this.isInitializing && this.selectedLocation) {
      this.loadDataForActiveTab();
    }
  }

  toggleDropdown() {
    this.closeAllDropdownsExceptPeriod();
    this.isCarWashDropdownOpen = false;
    this.isCampaignDropdownOpen = false;
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  /**
   * Selects a reporting period, closes the dropdown, marks filters as applied, and refreshes the active tab's statistics.
   * @example
   * electPeriod('Last 30 Days')
   * void
   * @param {string} period - The period to select.
   * @returns {void} No return value.
   */
  selectPeriod(period: string) {
    this.applyPeriodSelection(period);
  }

  onDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    if (data.customRange) {
      this.customDateRange = {
        startDate: data.customRange.startDate ? new Date(data.customRange.startDate) : null,
        endDate: data.customRange.endDate ? new Date(data.customRange.endDate) : null,
      };
    } else if (data.period !== 'Custom') {
      this.customDateRange = { startDate: null, endDate: null };
    }
    this.applyPeriodSelection(data.period);
  }

  closePeriodDropdown(): void {
    this.isDropdownOpen = false;
    this.isCarWashDropdownOpen = false;
    this.isCampaignDropdownOpen = false;
  }

  private applyPeriodSelection(period: string): void {
    this.selectedPeriod = period;
    this.closePeriodDropdown();
    this.hasAppliedFilters = true;

    if (this.activeTab === 'Customer') {
      this.loadCustomerStatistics();
    } else if (this.activeTab === 'Revenue') {
      this.loadRevenueStatistics();
      this.loadTopSellingServices();
      this.loadRevenueByServiceType();
      this.loadRevenueTrend();
      this.loadAverageRevenueTrend();
    } else if (this.activeTab === 'Car Wash') {
      if (this.selectedLocation) {
        this.loadCarWashStatistics();
        this.loadWashesByServiceType();
        this.loadWashCountTrend();
      }
    } else if (this.activeTab === 'Campaign Management') {
      if (this.selectedLocation) {
        this.loadCampaignStatistics();
      }
      if (this.selectedLocation && this.selectedCampaignName && this.selectedCampaignType) {
        this.loadCampaignPerformance();
        this.loadCampaignFunnel();
        this.loadCampaignOverallStatistics();
      }
    }
  }

  private getPeriodParam(): string {
    return buildPeriodApiParam(this.selectedPeriod, this.customDateRange);
  }

  closeDropdown() {
    this.isDropdownOpen = false;
  }

  closeAllDropdowns() {
    this.isDropdownOpen = false;
    this.isLocationDropdownOpen = false;
    this.isCarWashDropdownOpen = false;
    this.isCampaignDropdownOpen = false;
    this.isCampaignTypeDropdownOpen = false;
    this.isCampaignNameDropdownOpen = false;
  }

  private closeAllDropdownsExceptPeriod() {
    this.isLocationDropdownOpen = false;
    this.isCampaignTypeDropdownOpen = false;
    this.isCampaignNameDropdownOpen = false;
  }

  onDropdownClick(event: Event) {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const periodDropdown = target.closest('.period-dropdown-wrapper');
    const dateRangePicker = target.closest('.dropdown-menu');
    const customDatePicker = target.closest('.custom-date-picker');
    const primeCalendar = target.closest('.p-datepicker') || target.closest('.p-calendar');
    const primeCalendarPanel = target.closest('.p-datepicker-panel');

    if (
      periodDropdown ||
      dateRangePicker ||
      customDatePicker ||
      primeCalendar ||
      primeCalendarPanel
    ) {
      return;
    }

    this.closeAllDropdowns();
  }

  // ─── Location Handlers ────────────────────────────────────────────────────

  toggleLocationDropdown() {
    const wasOpen = this.isLocationDropdownOpen;
    this.closeAllDropdowns();
    this.isLocationDropdownOpen = !wasOpen;
  }

  /**
   * Selects a location, updates its label, and reloads dashboard data when the selection changes.
   * @example
   * electLocation('loc_123')
   * void
   * @param {string} locationId - The selected location identifier.
   * @returns {void} Does not return a value.
   */
  selectLocation(locationId: string) {
    const previousLocation = this.selectedLocation;
    this.selectedLocation = locationId || '';

    if (!locationId) {
      this.selectedLocationLabel = this.locationOptions.length > 0 ? '' : 'No Location';
    } else {
      const match = this.locationOptions.find(opt => opt.value === locationId);
      this.selectedLocationLabel = match ? match.label : locationId;
    }
    this.isLocationDropdownOpen = false;

    if (!this.isInitialLoad) this.hasAppliedFilters = true;

    if (this.selectedLocation) {
      this.locationService.setShopLocationById(this.selectedLocation);
    } else {
      this.locationService.clearShopLocation();
    }

    if (previousLocation !== this.selectedLocation) {
      this.isLoadingData = false;
      this.lastLoadedLocation = '';
      this.loadDataForActiveTab();
    }
  }

  // ─── Car Wash Handlers ────────────────────────────────────────────────────

  toggleCarWashDropdown() {
    this.closeAllDropdownsExceptPeriod();
    this.isCarWashDropdownOpen = !this.isCarWashDropdownOpen;
    this.isDropdownOpen = false;
    this.isCampaignDropdownOpen = false;
  }

  selectCarWashPeriod(period: string) {
    this.applyPeriodSelection(period);
  }

  // ─── Campaign Handlers ────────────────────────────────────────────────────

  toggleCampaignDropdown() {
    this.closeAllDropdownsExceptPeriod();
    this.isCampaignDropdownOpen = !this.isCampaignDropdownOpen;
    this.isDropdownOpen = false;
    this.isCarWashDropdownOpen = false;
  }

  /**
   * Selects a campaign period, closes the dropdown, and refreshes campaign data when required.
   * @example
   * electCampaignPeriod('Last 30 Days')
   * undefined
   * @param {string} period - The campaign period to select.
   * @returns {void} No return value.
   **/
  selectCampaignPeriod(period: string) {
    this.applyPeriodSelection(period);
  }

  toggleCampaignTypeDropdown() {
    this.closeAllDropdowns();
    this.isCampaignTypeDropdownOpen = !this.isCampaignTypeDropdownOpen;
  }

  selectCampaignType(type: string) {
    this.selectedCampaignType = type;
    this.isCampaignTypeDropdownOpen = false;
    if (this.selectedLocation && this.selectedCampaignName && this.selectedCampaignType) {
      this.loadCampaignPerformance();
      this.loadCampaignFunnel();
      this.loadCampaignOverallStatistics();
    }
  }

  toggleCampaignNameDropdown() {
    this.closeAllDropdowns();
    this.isCampaignNameDropdownOpen = !this.isCampaignNameDropdownOpen;
  }

  /**
   * Selects a campaign name, closes the dropdown, loads available platforms, and refreshes dashboard data when possible.
   * @example
   * electCampaignName('Summer Sale')
   * undefined
   * @param {string} name - The selected campaign name.
   * @returns {void} Does not return a value.
   */
  selectCampaignName(name: string) {
    this.selectedCampaignName = name;
    this.isCampaignNameDropdownOpen = false;
    if (!name) return;

    this.dashboardDataService
      .getCampaignPlatforms(name)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: platformOptions => {
          this.platformOptions = platformOptions;
          if (platformOptions.length > 0) {
            this.selectedCampaignType = platformOptions[0].label;
            if (this.selectedLocation && this.selectedCampaignName && this.selectedCampaignType) {
              this.loadCampaignPerformance();
              this.loadCampaignFunnel();
              this.loadCampaignOverallStatistics();
            }
          } else {
            this.selectedCampaignType = '';
          }
        },
        error: err => console.error('[Dashboard] selectCampaignName error:', err),
      });
  }

  // ─── Data Loading Orchestration ───────────────────────────────────────────

  /**
   * Loads the appropriate dashboard data for the currently selected location and active tab.
   * @example
   * loadDataForActiveTab()
   * void
   * @returns {void} No return value.
   */
  private loadDataForActiveTab(): void {
    if (!this.selectedLocation) return;

    const loadKey = `${this.selectedLocation}-${this.activeTab}`;
    const lastLoadKey = `${this.lastLoadedLocation}-${this.lastLoadedTab}`;
    if (!this.isInitializing && loadKey === lastLoadKey && this.isLoadingData) return;

    this.isLoadingData = true;
    this.lastLoadedLocation = this.selectedLocation;
    this.lastLoadedTab = this.activeTab;

    if (this.activeTab === 'Customer') {
      this.loadCustomerStatistics();
    } else if (this.activeTab === 'Car Wash') {
      this.loadCarWashStatistics();
      this.loadWashesByServiceType();
      this.loadWashCountTrend();
    } else if (this.activeTab === 'Campaign Management') {
      this.loadCampaignStatistics();
      this.loadCampaignsByLocation();
      if (this.selectedCampaignName && this.selectedCampaignType) {
        this.loadCampaignPerformance();
        this.loadCampaignFunnel();
        this.loadCampaignOverallStatistics();
      }
    } else {
      this.loadRevenueStatistics();
      this.loadTopSellingServices();
      this.loadRevenueByServiceType();
      this.loadRevenueTrend();
      this.loadAverageRevenueTrend();
    }

    setTimeout(() => {
      this.isLoadingData = false;
    }, 50);
  }

  // ─── Revenue Tab Loaders ──────────────────────────────────────────────────

  loadRevenueStatistics() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getRevenueStatistics(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ stats, rawData }) => {
          this.dashboardStats = stats;
          this.revenueStatsData = rawData;
        },
        error: err => console.error('Error loading revenue statistics:', err),
      });
  }

  loadTopSellingServices() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getTopSellingService(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: service => {
          this.topSellingService = service;
        },
        error: err => console.error('[Dashboard] loadTopSellingServices error:', err),
      });
  }

  /**
   * Loads and prepares revenue data by service type for the selected location and period.
   * @example
   * oadRevenueByServiceType()
   * undefined
   * @returns {void} Does not return a value.
   **/
  loadRevenueByServiceType() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getRevenueByServiceType(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ labels, data }) => {
          const backgroundColor = this.dashboardDataService.generateUniqueColors(labels.length);
          this.revenueByServiceData = {
            labels,
            datasets: [{ data, backgroundColor, borderWidth: 0, cutout: '60%' }],
          };
        },
        error: err => console.error('[Dashboard] loadRevenueByServiceType error:', err),
      });
  }

  /**
   * Loads and updates the revenue trend chart for the currently selected location and period.
   * @example
   * oadRevenueTrend()
   * undefined
   * @returns {void} Does not return a value.
   **/
  loadRevenueTrend() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getRevenueTrend(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ labels, data, dynamicMax }) => {
          this.revenueTrendData = {
            labels,
            datasets: [
              {
                label: 'Revenue',
                data,
                backgroundColor: 'rgba(116, 219, 155, 0.3)',
                borderColor: '#74DB9B',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#74DB9B',
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                pointRadius: 6,
                pointHoverRadius: 8,
                pointHoverBackgroundColor: '#74DB9B',
                pointHoverBorderColor: '#FFFFFF',
                pointHoverBorderWidth: 2,
              },
            ],
          };
          this.revenueTrendOptions = {
            ...this.revenueTrendOptions,
            scales: {
              ...this.revenueTrendOptions.scales,
              y: { ...this.revenueTrendOptions.scales.y, max: dynamicMax },
            },
          };
        },
        error: err => console.error('[Dashboard] loadRevenueTrend error:', err),
      });
  }

  /**
   * Loads and updates the average revenue trend data for the selected location and period.
   * @example
   * oadAverageRevenueTrend()
   * undefined
   * @returns {void} No return value.
   */
  loadAverageRevenueTrend() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getAverageRevenueTrend(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ title, labels, data, dynamicMax }) => {
          this.avgRevenuePerTeamData = {
            labels,
            datasets: [
              createDashboardLineDataset(title, data, '#FF8780', 'rgba(255, 135, 128, 0.15)', 2),
            ],
          };
          this.avgRevenuePerTeamOptions = {
            ...this.avgRevenuePerTeamOptions,
            scales: {
              ...this.avgRevenuePerTeamOptions.scales,
              y: { ...this.avgRevenuePerTeamOptions.scales.y, max: dynamicMax },
            },
          };
        },
        error: err => console.error('[Dashboard] loadAverageRevenueTrend error:', err),
      });
  }

  // ─── Customer Tab Loaders ─────────────────────────────────────────────────

  /**
   * Loads customer statistics and related dashboard metrics for the selected location and period.
   * @example
   * loadCustomerStatistics()
   * undefined
   * @returns {void} No return value.
   */
  loadCustomerStatistics() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getCustomerStatistics(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ stats, newVsReturning, rawData }) => {
          this.customerDashboardStats = stats;
          this.newVsReturningData = newVsReturning;
          this.customerStatsData = rawData;
        },
        error: err => console.error('Error loading customer statistics:', err),
      });

    this.loadCustomerVisitFrequency();
    this.loadCustomerTrend();
    this.loadWashesByServiceType();
    this.loadWashCountTrend();
  }

  loadCustomerTrend() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getCustomerTrend(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => this.updateCustomerTrendCharts(result),
        error: err => console.error('[Dashboard] loadCustomerTrend error:', err),
      });
  }

  /**
   * Loads and updates customer visit frequency chart data for the selected location and period.
   * @example
   * loadCustomerVisitFrequency()
   * undefined
   * @param {void} - This function takes no arguments.
   * @returns {void} Updates the component's visit frequency data or exits early if no location is selected.
   **/
  loadCustomerVisitFrequency() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getCustomerVisitFrequency(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ labels, data }) => {
          this.visitFrequencyData = {
            labels,
            datasets: [
              {
                data,
                backgroundColor: this.dashboardDataService.getVisitFrequencyColors(labels),
                borderWidth: 0,
                cutout: '60%',
              },
            ],
          };
        },
        error: err => console.error('[Dashboard] loadCustomerVisitFrequency error:', err),
      });
  }

  // ─── Car Wash Tab Loaders ─────────────────────────────────────────────────

  /**
   * Loads and updates the doughnut chart data for washes grouped by service type.
   * @example
   * oadWashesByServiceType()
   * void
   * @returns {void} No return value.
   **/
  loadWashesByServiceType() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getWashesByServiceType(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ labels, data }) => {
          const backgroundColor = this.dashboardDataService.generateUniqueColors(labels.length);
          this.washesByServiceTypeData = {
            labels,
            datasets: [{ data, backgroundColor, borderWidth: 0, cutout: '60%' }],
          };
        },
        error: err => console.error('[Dashboard] loadWashesByServiceType error:', err),
      });
  }

  /**
   * Loads and updates the wash count trend chart for the selected location and period.
   * @example
   * oadWashCountTrend()
   * Updates washesByHourData and washesByHourOptions with the latest trend data.
   * @returns {void} No return value.
   **/
  loadWashCountTrend() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getWashCountTrend(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ labels, data, dynamicMax }) => {
          this.washesByHourData = {
            labels,
            datasets: [
              createDashboardLineDataset('Washes', data, '#0CF0BB', 'rgba(41, 245, 150, 0.2)'),
            ],
          };
          this.washesByHourOptions = {
            ...this.washesByHourOptions,
            scales: {
              ...this.washesByHourOptions.scales,
              y: {
                ...this.washesByHourOptions.scales.y,
                ticks: {
                  ...this.washesByHourOptions.scales.y.ticks,
                  stepSize: Math.ceil(dynamicMax / 5),
                },
                max: dynamicMax,
              },
            },
          };
        },
        error: err => console.error('[Dashboard] loadWashCountTrend error:', err),
      });
  }

  loadCarWashStatistics() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getCarWashStatistics(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ stats, rawData }) => {
          this.carWashDashboardStats = stats;
          this.carWashStatsData = rawData;
        },
        error: err => console.error('Error loading car wash statistics:', err),
      });
  }

  // ─── Campaign Tab Loaders ─────────────────────────────────────────────────

  loadCampaignStatistics() {
    if (!this.selectedLocation) return;
    this.dashboardDataService
      .getCampaignStatistics(this.selectedLocation, this.getPeriodParam())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: stats => {
          this.campaignDashboardStats = stats;
        },
        error: err => console.error('[Dashboard] loadCampaignStatistics error:', err),
      });
  }

  /**
   * Loads campaign options for the currently selected location and updates related selections and data.
   * @example
   * oadCampaignsByLocation()
   * undefined
   * @returns {void} No return value.
   **/
  loadCampaignsByLocation() {
    if (!this.selectedLocation) {
      this.campaignNameOptions = [];
      this.platformOptions = [];
      return;
    }
    this.dashboardDataService
      .getCampaignsByLocation(this.selectedLocation)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ nameOptions, platformOptions, firstCampaignId }) => {
          this.campaignNameOptions = nameOptions;
          this.platformOptions = platformOptions;

          if (platformOptions.length > 0 && !this.selectedCampaignType) {
            this.selectedCampaignType = platformOptions[0].label;
          }

          const isCurrentSelectionValid = nameOptions.some(
            o => o.value === this.selectedCampaignName
          );
          if (!isCurrentSelectionValid && nameOptions.length > 0) {
            this.selectedCampaignName = firstCampaignId;
            if (platformOptions.length > 0 && !this.selectedCampaignType) {
              this.selectedCampaignType = platformOptions[0].value;
            }
            if (this.selectedLocation && this.selectedCampaignName && this.selectedCampaignType) {
              this.loadCampaignPerformance();
              this.loadCampaignFunnel();
              this.loadCampaignOverallStatistics();
            }
          } else if (nameOptions.length === 0) {
            this.selectedCampaignName = '';
          }
        },
        error: () => {
          this.campaignNameOptions = [];
          this.platformOptions = [];
          this.selectedCampaignName = '';
        },
      });
  }

  loadCampaignPerformance() {
    const params = this.getCampaignLoadParams();
    if (!params) return;
    this.dashboardDataService
      .getCampaignPerformance(
        params.platformId,
        this.selectedLocation,
        this.getPeriodParam(),
        params.campaignId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => this.updateCampaignPerformanceChart(result),
        error: () => {
          this.campaignPerformanceTrendData = null;
        },
      });
  }

  loadCampaignFunnel() {
    const params = this.getCampaignLoadParams();
    if (!params) return;
    this.dashboardDataService
      .getCampaignFunnel(
        params.platformId,
        this.selectedLocation,
        this.getPeriodParam(),
        params.campaignId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => this.updateCampaignFunnelChart(result),
        error: () => {
          this.campaignFunnelData = null;
        },
      });
  }

  loadCampaignOverallStatistics() {
    const params = this.getCampaignLoadParams();
    if (!params) return;
    this.dashboardDataService
      .getCampaignOverallStatistics(
        params.platformId,
        this.selectedLocation,
        this.getPeriodParam(),
        params.campaignId
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: stats => {
          this.campaignBottomStats = stats;
        },
        error: err => console.error('[Dashboard] loadCampaignOverallStatistics error:', err),
      });
  }

  // ─── Private Chart Update Methods ─────────────────────────────────────────

  private getCampaignLoadParams(): { platformId: string; campaignId: string } | null {
    if (!this.selectedLocation || !this.selectedCampaignName || !this.selectedCampaignType)
      return null;
    const selectedPlatform = this.platformOptions.find(
      opt => opt.label === this.selectedCampaignType
    );
    const platformId = selectedPlatform?.value || '';
    const campaignId = this.selectedCampaignName || '';
    if (!platformId || !campaignId) return null;
    return { platformId, campaignId };
  }

  /**
   * Updates the customer trend and monthly acquisition chart data and options using the provided trend result.
   * @example
   * updateCustomerTrendCharts(data)
   * void
   * @param {CustomerTrendResult} data - Customer trend data containing labels, new customers, and returning customers.
   * @returns {void} This function does not return a value.
   **/
  private updateCustomerTrendCharts(data: CustomerTrendResult) {
    const dynamicMax = calculateDynamicMax([data.newCustomers, data.returningCustomers]);

    this.newVsReturningTrendData = {
      labels: data.labels,
      datasets: [
        this.createNewVsReturningDataset(
          'New customers',
          data.newCustomers,
          '#8183F4',
          'rgba(129, 131, 244, 0.1)'
        ),
        this.createNewVsReturningDataset(
          'Returning customers',
          data.returningCustomers,
          '#FF8780',
          'rgba(255, 135, 128, 0.1)'
        ),
      ],
    };
    this.newVsReturningTrendOptions = {
      ...this.newVsReturningTrendOptions,
      scales: {
        ...this.newVsReturningTrendOptions.scales,
        y: { ...this.newVsReturningTrendOptions.scales.y, max: dynamicMax },
      },
    };

    this.monthlyAcquisitionData = {
      labels: data.labels,
      datasets: [
        {
          label: 'New customers',
          data: data.newCustomers,
          backgroundColor: '#8183F4',
          borderRadius: 2,
          borderSkipped: false,
          maxBarThickness: 34,
        },
        {
          label: 'Returning customers',
          data: data.returningCustomers,
          backgroundColor: '#FF8780',
          borderRadius: 2,
          borderSkipped: false,
          maxBarThickness: 34,
        },
      ],
    };
    this.monthlyAcquisitionOptions = {
      ...this.monthlyAcquisitionOptions,
      scales: {
        ...this.monthlyAcquisitionOptions.scales,
        y: { ...this.monthlyAcquisitionOptions.scales.y, max: dynamicMax },
      },
    };
  }

  /**
   * Updates the campaign performance chart data and Y-axis maximum based on the provided result.
   * @example
   * updateCampaignPerformanceChart(result)
   * void
   * @param {(ChartLabelsData & { dynamicMax: number }) | null} result - Chart label, data, and dynamic maximum values used to render the campaign performance chart.
   * @returns {void} Does not return a value.
   **/
  private updateCampaignPerformanceChart(
    result: (ChartLabelsData & { dynamicMax: number }) | null
  ) {
    if (result?.labels && result?.data?.length > 0) {
      this.campaignPerformanceTrendData = {
        labels: result.labels,
        datasets: [
          createDashboardLineDataset(
            'Performance',
            result.data,
            '#0CF0BB',
            'rgba(41, 245, 150, 0.2)'
          ),
        ],
      };
      if (this.campaignPerformanceTrendOptions?.scales?.y) {
        this.campaignPerformanceTrendOptions.scales.y.max = result.dynamicMax;
      }
    } else {
      this.campaignPerformanceTrendData = null;
    }
  }

  /**
   * Updates the campaign funnel chart data when valid labels and data are provided.
   * @example
   * updateCampaignFunnelChart(result)
   * this.campaignFunnelData = { labels: [...], datasets: [...] } or null
   * @param {ChartLabelsData | null} result - Chart labels and data used to build the campaign funnel chart.
   * @returns {void} Does not return a value.
   **/
  private updateCampaignFunnelChart(result: ChartLabelsData | null) {
    if (result?.labels && result?.data?.length > 0) {
      this.campaignFunnelData = {
        labels: result.labels,
        datasets: [
          {
            label: 'Campaign Funnel',
            data: result.data,
            borderColor: '#7D22D8',
            backgroundColor: 'rgba(125, 34, 216, 0.15)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            ...getDashboardLineChartPointStyle('#7D22D8', { pointRadius: 4 }),
          },
        ],
      };
    } else {
      this.campaignFunnelData = null;
    }
  }

  private createNewVsReturningDataset(
    label: string,
    data: any[],
    color: string,
    backgroundColor: string
  ) {
    return {
      label,
      data,
      borderColor: color,
      backgroundColor,
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBorderWidth: 0,
      pointHoverBorderColor: color,
      pointHoverBorderWidth: 2,
    };
  }

  private isDashboardRoute(): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return currentUrl === '/dashboard' || currentUrl.startsWith('/dashboard');
  }

  // ─── Chart Initialization ─────────────────────────────────────────────────

  /**
   * Initializes the dashboard chart data and configuration for revenue-related charts.
   * @example
   * nitChartData()
   * undefined
   * @returns {void} Initializes chart data/options and delegates initialization to other dashboard chart setup methods.
   */
  initChartData() {
    this.revenueByServiceData = {
      labels: [],
      datasets: [{ data: [], backgroundColor: [], borderWidth: 0, cutout: '60%' }],
    };
    this.revenueByServiceOptions = getDashboardDoughnutChartOptions();

    const revenueInitialData = [20, 45, 38, 65, 90, 75, 85];
    this.revenueTrendData = {
      labels: ['12 am', '1 am', '2 am', '3 am', '4 am', '5 am', '6 am'],
      datasets: [
        createDashboardLineDataset(
          'Revenue',
          revenueInitialData,
          '#0CF0BB',
          'rgba(41, 245, 150, 0.2)'
        ),
      ],
    };
    this.revenueTrendOptions = getDashboardLineChartOptionsWithCustomTooltip(
      (context: any) =>
        getDashboardTooltipLabelWithPercentChangeAndFormat(context, {
          formatValue: v => '$' + v.toLocaleString(),
        }),
      calculateDynamicMax(revenueInitialData),
      {
        yTickCallback: (value: any) =>
          value >= 1000 ? (value / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : String(value),
      }
    );

    const avgRevenueInitialData = [12, 18, 22, 28, 35, 40, 46, 52, 60, 68, 75, 84];
    this.avgRevenuePerTeamData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      datasets: [
        createDashboardLineDataset(
          'Avg. revenue/team',
          avgRevenueInitialData,
          '#FF8780',
          'rgba(255, 135, 128, 0.15)',
          2
        ),
      ],
    };
    this.avgRevenuePerTeamOptions = getDashboardLineChartOptionsWithCustomTooltip(
      (context: any) =>
        getDashboardTooltipLabelWithPercentChangeAndFormat(context, { formatValue: v => '$' + v }),
      calculateDynamicMax(avgRevenueInitialData),
      { yTickCallback: (value: any) => '$' + value, yPadding: 4 }
    );

    this.initCustomerCharts();
    this.initCarWashCharts();
    this.initCampaignCharts();
  }

  /**
   * Initializes the dashboard chart data and options for customer trends, visit frequency, and monthly acquisition.
   * @example
   * initCustomerCharts()
   * undefined
   * @param {void} - This function takes no parameters.
   * @returns {void} No return value.
   **/
  private initCustomerCharts() {
    const newData = [800, 950, 750, 1100, 1300, 1150, 1400, 1200, 1350, 1500, 1250, 1600];
    const returningData = [600, 800, 650, 900, 1000, 850, 1100, 950, 1050, 1200, 1000, 1300];
    const monthLabels = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    this.newVsReturningTrendData = {
      labels: monthLabels,
      datasets: [
        this.createNewVsReturningDataset(
          'New customers',
          newData,
          '#8183F4',
          'rgba(129, 131, 244, 0.1)'
        ),
        this.createNewVsReturningDataset(
          'Returning customers',
          returningData,
          '#FF8780',
          'rgba(255, 135, 128, 0.1)'
        ),
      ],
    };
    this.newVsReturningTrendOptions = getDashboardLineChartOptionsWithPercentChange(
      'right',
      calculateDynamicMax([newData, returningData])
    );

    this.visitFrequencyData = {
      labels: ['7+ visits', '4-6 visits', '2-3 visits', '1 visit'],
      datasets: [
        {
          data: [35, 28, 22, 15],
          backgroundColor: ['#8183F4', '#FF8780', '#F2D066', '#76DB9B'],
          borderWidth: 0,
          cutout: '60%',
        },
      ],
    };
    this.visitFrequencyOptions = getDashboardDoughnutChartOptions();

    const acquisitionNew = [1200, 1350, 980, 1450, 1600, 1250, 1700, 1400, 1550, 1800, 1300, 1900];
    const acquisitionReturning = [
      800, 950, 650, 1100, 1200, 850, 1300, 950, 1050, 1400, 1000, 1300,
    ];
    this.monthlyAcquisitionData = {
      labels: monthLabels,
      datasets: [
        {
          label: 'New customers',
          data: acquisitionNew,
          backgroundColor: '#8183F4',
          borderRadius: 2,
          borderSkipped: false,
          maxBarThickness: 34,
        },
        {
          label: 'Returning customers',
          data: acquisitionReturning,
          backgroundColor: '#FF8780',
          borderRadius: 2,
          borderSkipped: false,
          maxBarThickness: 34,
        },
      ],
    };
    this.monthlyAcquisitionOptions = getDashboardLineChartOptionsWithPercentChange(
      'left',
      calculateDynamicMax([acquisitionNew, acquisitionReturning]),
      { yPadding: 4, yBorderDash: [2, 2], barBorderRadius: 2 }
    );
  }

  /**
   * Initializes the car wash chart data and configuration for hourly and service type dashboards.
   * @example
   * initCarWashCharts()
   * void
   * @returns {void} No return value.
   **/
  private initCarWashCharts() {
    this.washesByHourData = {
      labels: [],
      datasets: [createDashboardLineDataset('Washes', [], '#0CF0BB', 'rgba(41, 245, 150, 0.2)')],
    };
    this.washesByHourOptions = getDashboardWashesByHourChartOptions(25);

    this.washesByServiceTypeData = {
      labels: [],
      datasets: [{ data: [], backgroundColor: [], borderWidth: 0, cutout: '60%' }],
    };
    this.washesByServiceTypeOptions = getDashboardDoughnutChartOptions();
  }

  /**
   * Initializes campaign chart data and configuration options for performance trend and funnel charts.
   * @example
   * initCampaignCharts()
   * undefined
   * @returns {void} No return value.
   **/
  private initCampaignCharts() {
    this.campaignPerformanceTrendData = null;
    this.campaignFunnelData = null;

    this.campaignPerformanceTrendOptions = getDashboardLineChartOptionsWithCustomTooltip(
      (context: any) =>
        getDashboardTooltipLabelWithPercentChangeAndFormat(context, { formatValue: v => v + '%' }),
      100,
      { yTickCallback: (value: any) => value + '%', yPadding: 10 }
    );

    this.campaignFunnelOptions = {
      plugins: {
        legend: { display: false },
        tooltip: getDashboardChartTooltipWithPercentOfTotal(true),
      },
      scales: {
        x: DASHBOARD_CHART_X_SCALE,
        y: DASHBOARD_CHART_Y_SCALE_HIDDEN,
      },
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      layout: { padding: { top: 10, bottom: 10, left: 10, right: 10 } },
    };
  }
}
